/** SMS message types used in tariff / buy-template billing maps. */
export const SMS_TYPES = [
  'ads',
  'ads_custom',
  'ads_template',
  'service',
  'identification',
  'information',
  'authorization',
] as const;

export type SmsType = (typeof SMS_TYPES)[number];

/** Wire/API numeric ids (1-based, order matches {@link SMS_TYPES}). */
export const SMS_TYPE_ID: Record<SmsType, number> = {
  ads: 1,
  ads_custom: 2,
  ads_template: 3,
  service: 4,
  identification: 5,
  information: 6,
  authorization: 7,
};

const SMS_TYPE_BY_ID: Record<number, SmsType> = Object.fromEntries(
  Object.entries(SMS_TYPE_ID).map(([name, id]) => [Number(id), name as SmsType]),
) as Record<number, SmsType>;

const SMS_TYPE_LABELS: Record<SmsType, string> = {
  ads: 'Рекламные',
  ads_custom: 'Рекламные (кастом)',
  ads_template: 'Рекламные (шаблон)',
  service: 'Сервисные',
  identification: 'Идентификация',
  information: 'Информационные',
  authorization: 'Авторизация',
};

export function isSmsType(value: string): value is SmsType {
  return (SMS_TYPES as readonly string[]).includes(value);
}

export function isSmsTypeId(id: number): boolean {
  return Object.prototype.hasOwnProperty.call(SMS_TYPE_BY_ID, id);
}

export function smsTypeToId(type: SmsType): number {
  return SMS_TYPE_ID[type];
}

export function smsTypeFromId(id: number): SmsType | undefined {
  return SMS_TYPE_BY_ID[id];
}

/** Parse wire map key (numeric string or legacy enum name) → type id. */
export function parseSmsTypeWireKey(key: string): number | null {
  if (isSmsType(key)) return smsTypeToId(key);
  const n = Number(key);
  // Accept any positive int — message_type ids come from SMS API, not fixed 1..7.
  if (Number.isInteger(n) && n > 0) return n;
  return null;
}

export function smsTypeWireKey(id: number): string {
  return String(id);
}

export function smsTypeIdLabel(id: number): string {
  const type = smsTypeFromId(id);
  return type ? SMS_TYPE_LABELS[type] : String(id);
}

export function smsTypeLabel(type: string | number): string {
  if (typeof type === 'number') return smsTypeIdLabel(type);
  const parsed = parseSmsTypeWireKey(type);
  if (parsed != null) return smsTypeIdLabel(parsed);
  return isSmsType(type) ? SMS_TYPE_LABELS[type] : type;
}

export const SMS_TYPE_OPTIONS = SMS_TYPES.map((name) => ({
  value: smsTypeToId(name),
  text: SMS_TYPE_LABELS[name],
}));
